import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileCheck, Gavel, Receipt, Building2, Database, Files, ChevronLeft, ChevronRight, GitMerge, Sparkles, IndianRupee, TrendingUp, AlertTriangle, Gauge, ShieldCheck } from "lucide-react";
import { Reveal } from "@/lib/motion";

const SIGNALS = [
  { icon: FileCheck, label: "CLU notifications" },
  { icon: Gavel, label: "Tender movements" },
  { icon: Receipt, label: "Registry transactions" },
  { icon: Building2, label: "Infrastructure approvals" },
  { icon: Database, label: "Government databases" },
  { icon: Files, label: "Public records" },
];

const SCORE_INPUTS = [
  { icon: IndianRupee, label: "Value today" },
  { icon: TrendingUp, label: "Value if signals complete" },
  { icon: AlertTriangle, label: "Risk they don't" },
];

const SLIDES = [
  {
    step: "Step 01",
    kicker: "Collects Data & Market Signals",
    title: "What governments scatter across a thousand portals, ROCI brings together.",
  },
  {
    step: "Step 02",
    kicker: "Reads the Story",
    title: "Alone, these signals are noise. Together, they reveal what changes next.",
  },
  {
    step: "Step 03",
    kicker: "Calculate the Outcome",
    title: "The system doesn't guess. It calculates.",
    body: "What is this land worth today? What does ROCI estimate it could be worth once these signals complete? What's the risk they don't? It turns those answers into a single score, factoring in timeline and risk, that tells us which parcels are worth pursuing.",
  },
];

const variants = {
  enter: (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
};

function RightPanel({ index }) {
  if (index === 0) {
    return (
      <div className="w-full">
        <p className="text-xs tracking-[0.25em] uppercase text-brand-orange mb-6 text-left">Raw land signals</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {SIGNALS.map((s) => (
            <div key={s.label} className="flex flex-col items-center justify-center gap-3 bg-brand-offwhite/5 border border-brand-offwhite/10 rounded-sm px-4 py-8 text-center hover:border-brand-orange/40 transition-colors">
              <s.icon className="w-9 h-9 text-brand-orange" strokeWidth={1.5} />
              <span className="text-sm text-brand-offwhite/85">{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (index === 1) {
    return (
      <div className="w-full">
        <p className="text-xs tracking-[0.25em] uppercase text-brand-orange mb-6 text-left">Signals into pattern</p>
        <div className="grid grid-cols-3 gap-3 mb-6">
          {SIGNALS.map((s, k) => (
            <motion.div key={s.label} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: k * 0.06 }}
              className="flex flex-col items-center justify-center gap-2 bg-brand-offwhite/5 border border-brand-offwhite/10 rounded-sm p-4 text-center">
              <s.icon className="w-6 h-6 text-brand-orange" strokeWidth={1.5} />
              <span className="text-[11px] leading-tight text-brand-offwhite/60">{s.label}</span>
            </motion.div>
          ))}
        </div>
        <div className="flex items-center text-brand-offwhite/40 mb-4"><GitMerge className="w-6 h-6 rotate-90" /></div>
        <div className="flex items-center gap-3 bg-brand-orange/15 border border-brand-orange/40 rounded-sm px-5 py-4">
          <Sparkles className="w-5 h-5 text-brand-orange shrink-0" />
          <span className="text-sm font-medium text-brand-offwhite">Zones where land is likely to transform next</span>
        </div>
      </div>
    );
  }
  return (
    <div className="w-full">
      <p className="text-xs tracking-[0.25em] uppercase text-brand-orange mb-6 text-left">Weighing the outcome</p>
      <div className="grid grid-cols-3 gap-3 mb-6">
        {SCORE_INPUTS.map((s, k) => (
          <motion.div key={s.label} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: k * 0.06 }}
            className="flex flex-col items-center justify-center gap-2 bg-brand-offwhite/5 border border-brand-offwhite/10 rounded-sm p-4 text-center">
            <s.icon className="w-6 h-6 text-brand-orange" strokeWidth={1.5} />
            <span className="text-[11px] leading-tight text-brand-offwhite/60">{s.label}</span>
          </motion.div>
        ))}
      </div>
      <div className="flex items-center text-brand-offwhite/40 mb-4"><GitMerge className="w-6 h-6 rotate-90" /></div>
      <div className="flex items-center gap-3 bg-brand-orange/15 border border-brand-orange/40 rounded-sm px-5 py-4">
        <Gauge className="w-5 h-5 text-brand-orange shrink-0" />
        <span className="text-sm font-medium text-brand-offwhite">One score that ranks which parcels are worth pursuing</span>
      </div>
    </div>
  );
}

export default function RociEngine() {
  const [[index, dir], setState] = useState([0, 0]);
  const go = (n) => setState([n, n > index ? 1 : -1]);
  const slide = SLIDES[index];

  return (
    <section id="roci" className="bg-brand-paper py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header: left headline + Patent Applied card on the far right */}
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-8 mb-14">
          <Reveal className="max-w-2xl">
            <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">Meet the ROCI Engine</span>
            <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-brand-ink leading-[1.05]">A system built on data, not guesswork.</h2>
          </Reveal>
          <Reveal>
            <div data-testid="patent-badge" className="shrink-0 lg:max-w-xs bg-brand-dark border border-brand-orange/40 rounded-sm p-7 relative overflow-hidden">
              <div className="grain" />
              <div className="relative z-10">
                <ShieldCheck className="w-6 h-6 text-brand-orange mb-3" />
                <p className="font-display font-semibold text-3xl sm:text-4xl tracking-tight text-brand-offwhite leading-[1.05]">Patent Applied</p>
                <p className="mt-2 text-sm text-brand-offwhite/60">Application 202611089476</p>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Body: one dark box containing tiles (left) + infographic (right) */}
        <div className="bg-brand-dark rounded-sm overflow-hidden relative" data-testid="roci-carousel">
          <div className="grain" />
          <div className="relative z-10">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center p-8 sm:p-10 lg:p-14">
              {/* Left: step tiles / carousel content */}
              <div className="relative min-h-[420px]">
                <AnimatePresence mode="wait" custom={dir}>
                  <motion.div key={index} custom={dir} variants={variants} initial="enter" animate="center" exit="exit"
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="flex flex-col items-start text-left">
                    <div className="flex items-center gap-3 mb-6">
                      <span className="font-mono text-sm text-brand-orange">{slide.step}</span>
                      <span className="h-px w-8 bg-brand-offwhite/20" />
                      <span className="text-xs tracking-[0.2em] uppercase text-brand-offwhite/50">{slide.kicker}</span>
                    </div>
                    <h3 className="font-display font-semibold text-2xl sm:text-3xl tracking-tight text-brand-offwhite leading-[1.1]">{slide.title}</h3>
                    {slide.body && (
                      <p className="mt-5 text-brand-offwhite/60 leading-relaxed text-[15px]">{slide.body}</p>
                    )}
                    <div className="mt-10 w-full">
                      <RightPanel index={index} />
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Right: illustrated infographic, centered in its column, no frame */}
              <div className="flex items-center justify-center">
                <img
                  data-testid="roci-infographic"
                  src="/assets/ds-detailed-explainer.webp"
                  alt="Demand-Supply Intelligence: Tenders and sale-purchase records feeding the ROCI engine to produce a verified historical result of 24 deals, 92 crore deployed and 48% average return."
                  width={1600}
                  height={893}
                  loading="lazy"
                  className="w-full h-auto rounded-sm"
                />
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-4 px-8 sm:px-10 lg:px-14 pb-8">
              <button data-testid="roci-prev" onClick={() => go(index === 0 ? SLIDES.length - 1 : index - 1)} aria-label="Previous"
                className="w-11 h-11 rounded-full border border-brand-offwhite/25 flex items-center justify-center text-brand-offwhite hover:bg-brand-offwhite hover:text-brand-ink transition-colors">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex gap-2" data-testid="roci-controls">
                {SLIDES.map((_, k) => (
                  <button key={k} data-testid={`roci-dot-${k}`} onClick={() => go(k)} aria-label={`Slide ${k + 1}`}
                    className={`h-1.5 rounded-full transition-all duration-300 ${k === index ? "w-10 bg-brand-orange" : "w-4 bg-brand-offwhite/25 hover:bg-brand-offwhite/50"}`} />
                ))}
              </div>
              <button data-testid="roci-next" onClick={() => go(index === SLIDES.length - 1 ? 0 : index + 1)} aria-label="Next"
                className="w-11 h-11 rounded-full border border-brand-offwhite/25 flex items-center justify-center text-brand-offwhite hover:bg-brand-offwhite hover:text-brand-ink transition-colors">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
